import * as z from "zod";
import { decimal, ean, version } from "../index";
import { MAX_LINE_UNITS } from "../til";
import { packingListSources, threeWayResults } from "./enums";
import { MAX_PURCHASE_ORDER_LINES } from "./purchase-order-line";

// The packing list and the three-way check of an inbound (50-api/procurement.md §2.6; FR-M11-02, 05…07, ADR-131):
// Logistics types the lines of the packing list from its PDF, the domain compares PO, packing list and invoice line by
// line, and Operations may force a failed check with its reason.

/** A shipment of a PO with every line split in a few cartons. */
export const MAX_PACKING_LIST_LINES = MAX_PURCHASE_ORDER_LINES;

/**
 * A line of the packing list as Logistics reads it: the PO line it is, or its EAN, which finds the line of the inbound
 * with that SKU; neither of them found is a line the PO does not have, and the check says so.
 */
export const packingListLineInput = z
  .object({
    poLineId: z.uuid().optional(),
    ean: ean.optional(),
    description: z.string().trim().min(1).max(500).optional(),
    qty: z.number().int().min(1).max(MAX_LINE_UNITS),
    cartons: z.number().int().min(0).max(1_000_000).optional(),
    cartonRef: z.string().trim().min(1).max(60).optional(),
  })
  .refine((line) => line.poLineId !== undefined || line.ean !== undefined, {
    message: "a PO line or an EAN",
    path: ["poLineId"],
  })
  .meta({ description: "Either `poLineId` or `ean` is required." });
export type PackingListLineInput = z.output<typeof packingListLineInput>;

/** The units a packing list holds at most: what the check counts in an `integer`. */
const MAX_PACKING_LIST_UNITS = 2_147_483_647;

/** All the lines of the packing list: they replace the ones saved before, and the check is to be done again. */
export const savePackingListInput = z.object({
  inboundId: z.uuid(),
  version,
  lines: z
    .array(packingListLineInput)
    .min(1)
    .max(MAX_PACKING_LIST_LINES)
    .refine((lines) => lines.reduce((sum, line) => sum + line.qty, 0) <= MAX_PACKING_LIST_UNITS, {
      message: `at most ${MAX_PACKING_LIST_UNITS} units`,
    }),
});
export type SavePackingListInput = z.input<typeof savePackingListInput>;
export const savePackingListBody = savePackingListInput.omit({ inboundId: true });

/** A line of the packing list, with the PO line it was matched to. */
export const packingListLineDto = z.object({
  id: z.uuid(),
  poLineId: z.uuid().nullable(),
  lineNo: z.number().int().nullable(),
  skuId: z.uuid().nullable(),
  skuCode: z.string().nullable(),
  ean: z.string().nullable(),
  description: z.string().nullable(),
  qty: z.number().int(),
  cartons: z.number().int().nullable(),
  cartonRef: z.string().nullable(),
  /** Typed by Logistics, or read by the extraction tool (ADR-233). */
  source: z.enum(packingListSources),
  /** How sure the tool was of a line it read, from 0 to 1; null on a line typed. Only Tomorrow reads it. */
  extractionConfidence: decimal.nullable().optional(),
});

/**
 * What a check finds (ADR-131):
 * - on a line of the inbound: `quantity_mismatch` (the packing list does not have the declared units), `missing_ean`,
 *   `missing_commodity_code`, `missing_manufacturer` (the SKU lacks what the inbound DDT needs, FR-M4-07);
 * - on a line of the packing list: `unmatched_line` (no line of the inbound), `ean_mismatch` (another EAN than its
 *   SKU's), `duplicate_ean` (the same EAN on lines of different PO lines);
 * - on the inbound: `invoice_total_mismatch` (the invoice is not the packing list at the PO cost, to the cent);
 *   `invoice_gross_mismatch` (its total is not its net total plus its VAT) and `invoice_vat_mismatch` (its VAT is not
 *   the VAT of the lines at the rate of the VAT code of the supplier, spec v0.3 §7.4.3, ADR-256).
 */
export const threeWayIssueRules = [
  "quantity_mismatch",
  "missing_ean",
  "missing_commodity_code",
  "missing_manufacturer",
  "unmatched_line",
  "ean_mismatch",
  "duplicate_ean",
  "invoice_total_mismatch",
  "invoice_gross_mismatch",
  "invoice_vat_mismatch",
] as const;
export type ThreeWayIssueRule = (typeof threeWayIssueRules)[number];

export const threeWayIssue = z.object({
  rule: z.enum(threeWayIssueRules),
  poLineId: z.uuid().optional(),
  lineNo: z.number().int().optional(),
  packingListLineId: z.uuid().optional(),
  ean: z.string().optional(),
});
export type ThreeWayIssue = z.output<typeof threeWayIssue>;

/** A line of the inbound in the check: what the PO, the supplier and the packing list say of it. */
export const threeWayLine = z.object({
  poLineId: z.uuid(),
  lineNo: z.number().int(),
  skuCode: z.string().nullable(),
  qtyDeclared: z.number().int(),
  qtyPackingList: z.number().int(),
  unitCost: decimal,
  /** The units of the packing list at the PO cost: what the invoice should say of the line. */
  amount: decimal,
});
export type ThreeWayLine = z.output<typeof threeWayLine>;

/** `three_way_check.details`: the comparison line by line and what did not match. */
export const threeWayDetails = z.object({ lines: z.array(threeWayLine), issues: z.array(threeWayIssue) });
export type ThreeWayDetails = z.output<typeof threeWayDetails>;

/** The last check of an inbound. Amounts in the currency of the PO. */
export const threeWayCheckDto = z.object({
  id: z.uuid(),
  result: z.enum(threeWayResults),
  /** The units of the packing list at the PO cost, over the lines of the inbound. */
  poTotal: decimal,
  packingListTotalQty: z.number().int(),
  invoiceTotal: decimal,
  currency: z.string().nullable(),
  details: threeWayDetails,
  /** Why Operations forced a failed check: only Tomorrow reads it (ADR-170). */
  overrideReason: z.string().nullable().optional(),
  checkedBy: z.string().nullable(),
  checkedAt: z.iso.datetime(),
});
